import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';

/**
 * Fixtures for the "in-progress" Tuition Assistance demo: an active participant
 * with an active eligibility on a launched program template, one active program
 * instance, and one in-progress application. Shapes follow the proven demo
 * push payloads from the TAS getting-started flow.
 *
 * Served through the demo handler for the store-routed TAS pages
 * (tas/getting-started, tas/dashboard-v3, tas/programs, tas/applications/*).
 */

const DEMO_CODE = 'DEMO_PROGRAM';
const PROGRAM_NAME = 'Acme Tuition Assistance';

const ELIGIBILITY_RESOURCE: JsonApiResource = {
  type: 'tas-eligibility',
  id: 'demo-tas-eligibility-1',
  attributes: { code: DEMO_CODE, state: 'TAS.EligibilityState.ACTIVE' },
};

const TEMPLATE_RESOURCE: JsonApiResource = {
  type: 'tas-program-template',
  id: 'demo-tas-template-1',
  attributes: {
    code: DEMO_CODE,
    state: 'TAS.ProgramTemplateState.LAUNCHED',
    fields: {
      PROGRAM_NAME: { values: [PROGRAM_NAME] },
      PROGRAM_DESCRIPTION: { values: ['Employer-sponsored tuition assistance.'] },
      TYPE: { values: ['TAS.ProgramType.4.2'] },
      DEPENDENT_PROGRAM: { values: [false] },
      MULTIPLE_INSTANCE_PROGRAM: { values: [false] },
      ELIGIBILITY_WAITING_PERIOD: { values: [0] },
    },
    faqs: [],
    customFields: [],
    approverInstructions: [],
    isTrial: false,
  },
  relationships: {
    conditions: { data: [] },
    tasEligibilities: { data: [] },
    tasGroupings: { data: [] },
    tasProgramInstances: { data: [] },
  },
};

const INSTANCE_RESOURCE: JsonApiResource = {
  type: 'tas-program-instance',
  id: 'demo-tas-instance-1',
  attributes: {
    state: 'TAS.ProgramInstanceState.PROGRAM_APPROVED',
  },
  relationships: {
    tasProgramTemplate: { data: { type: 'tas-program-template', id: 'demo-tas-template-1' } },
  },
};

const PARTICIPANT_RESOURCE: JsonApiResource = {
  type: 'tas-participant',
  id: 'demo-tas-participant-1',
  attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
  relationships: {
    tasEligibilities: { data: [{ type: 'tas-eligibility', id: 'demo-tas-eligibility-1' }] },
    tasGroupings: { data: [] },
  },
};

const APPLICATION_RESOURCE: JsonApiResource = {
  type: 'tas-application',
  id: 'demo-tas-application-1',
  attributes: {
    state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    fields: {
      COURSE_NAME: { values: ['Intro to Data Science'] },
      REQUESTED_TOTAL: { values: [1500] },
    },
    customFields: [],
  },
  relationships: {
    tasProgramInstance: { data: { type: 'tas-program-instance', id: 'demo-tas-instance-1' } },
  },
};

// company-setting drives several dashboard reads
// (currentEmployee.company.companySetting.tuitionAssistance.*).
const COMPANY_SETTING_RESOURCE: JsonApiResource = {
  type: 'company-setting',
  id: 'demo-tas-companysetting-1',
  attributes: {
    tuitionAssistance: {
      hideAnnualBenefitsCard: false,
      isDependentProgramsEnabled: false,
    },
  },
};

const COMPANY_RESOURCE: JsonApiResource = {
  type: 'company',
  id: 'demo-tas-company-1',
  attributes: { name: 'Acme Corporation', isActive: true },
  relationships: {
    companySetting: { data: { type: 'company-setting', id: 'demo-tas-companysetting-1' } },
  },
};

// Employee findRecord (include tas-participant,company) — the dashboard reads
// employee.tasParticipant to decide whether to run eligibility checks, so the
// participant must be sideloaded for the demo program to surface. This doc is
// ALSO seeded as the demo session identity (see DemoModeService.demoCurrentEmployee),
// so it carries the company + company-setting the dashboard template reads.
const EMPLOYEE_RESOURCE: JsonApiResource = {
  type: 'employee',
  id: 'demo-tas-employee-1',
  attributes: { firstName: 'Daffy', lastName: 'Duck', isActive: true },
  relationships: {
    tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
    company: { data: { type: 'company', id: 'demo-tas-company-1' } },
  },
};

const participantCollection: JsonApiDoc = {
  data: [PARTICIPANT_RESOURCE],
  included: [ELIGIBILITY_RESOURCE],
};
const employeeDoc: JsonApiDoc = {
  data: EMPLOYEE_RESOURCE,
  included: [
    PARTICIPANT_RESOURCE,
    ELIGIBILITY_RESOURCE,
    COMPANY_RESOURCE,
    COMPANY_SETTING_RESOURCE,
  ],
};
const templateCollection: JsonApiDoc = { data: [TEMPLATE_RESOURCE] };
const templateDoc: JsonApiDoc = { data: TEMPLATE_RESOURCE };
const instanceCollection: JsonApiDoc = { data: [INSTANCE_RESOURCE], included: [TEMPLATE_RESOURCE] };
const instanceDoc: JsonApiDoc = { data: INSTANCE_RESOURCE, included: [TEMPLATE_RESOURCE] };
// Sideload the instance AND its template so async:false
// tasProgramInstance.tasProgramTemplate resolves on the application show/confirmation routes.
const applicationDoc: JsonApiDoc = {
  data: APPLICATION_RESOURCE,
  included: [INSTANCE_RESOURCE, TEMPLATE_RESOURCE],
};

// Empty OpenSearch result — a valid "enrolled, no applications yet" state.
// executeSearchRequest is a store.request builder (POST, SkipCache), so the
// demo HANDLER intercepts it (not the fetch interceptor); callers read .content.
const EMPTY_SEARCH = { hits: { total: { value: 0, relation: 'eq' }, hits: [] } };

export const IN_PROGRESS_TAS_MATCHERS: MatcherFixture[] = [
  { matcher: { kind: 'builder', urlIncludes: '/api/v2/search' }, content: EMPTY_SEARCH },
  { matcher: { kind: 'op', op: 'findRecord', type: 'employee' }, docs: [employeeDoc] },
  { matcher: { kind: 'op', op: 'query', type: 'tas-participant' }, docs: [participantCollection] },
  // The dashboard runs a SECOND tas-program-template query for trial templates
  // (admins only). It must NOT return the launched demo program, or the eligible
  // list shows a duplicate card. This filter-specific matcher precedes the
  // generic one below (first match wins) and serves an empty collection.
  {
    matcher: {
      kind: 'op',
      op: 'query',
      type: 'tas-program-template',
      where: (query) => (query?.filter as { isTrial?: boolean } | undefined)?.isTrial === true,
    },
    docs: [{ data: [] }],
  },
  {
    matcher: { kind: 'op', op: 'query', type: 'tas-program-template' },
    docs: [templateCollection],
  },
  { matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-template' }, docs: [templateDoc] },
  {
    matcher: { kind: 'op', op: 'query', type: 'tas-program-instance' },
    docs: [instanceCollection],
  },
  // tas/programs loads its primary model via findRecord('tas-program-instance', ...).
  {
    matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-instance' },
    docs: [instanceDoc],
  },
  { matcher: { kind: 'op', op: 'query', type: 'tas-group' }, docs: [{ data: [] }] },
  { matcher: { kind: 'op', op: 'findRecord', type: 'tas-application' }, docs: [applicationDoc] },
];

// --- Raw-fetch (seam B) matchers -------------------------------------------
// The TAS dashboard reaches these directly via fetch (bypassing the store):
// limits status and the tas-participant eligibility endpoints. (OpenSearch is
// NOT here — executeSearchRequest is a store.request builder, so it is served
// by the demo handler via the '/api/v2/search' builder matcher above.)

const limitsDetail = (limit: number | null, usage: number) => ({
  annual_limit: limit,
  annual_usage: usage,
  available: limit === null ? null : limit - usage,
  hard: limit !== null,
  limit,
  one_month_limit: null,
  one_month_usage: 0,
  six_month_limit: null,
  six_month_usage: 0,
  taxable_limit: null,
  three_month_limit: null,
  three_month_usage: 0,
  usage,
});

const DEMO_LIMITS = {
  data: [
    {
      annual_start_date: '2026-01-01',
      annual_end_date: '2026-12-31',
      available: 3750,
      available_with_override: 3750,
      details: {
        company: limitsDetail(null, 0),
        product: limitsDetail(5250, 1500),
        batch: limitsDetail(null, 0),
        scheme: limitsDetail(null, 0),
      },
      employee_id: 'demo-tas-employee-1',
      hard_limit: 'product_annual',
      limit_capacity: 'TAS.LimitCapacity.MONETARY',
      product: 'TA',
      scheme: null,
      soft_limit: 'none',
    },
  ],
};

export const IN_PROGRESS_TAS_FETCH_MATCHERS: FetchMatcher[] = [
  { urlIncludes: '/dynamic-eligibility', json: { [DEMO_CODE]: true } },
  { urlIncludes: '/periods-of-eligibility', json: { [DEMO_CODE]: [['2024-01-01', '2027-12-31']] } },
  { urlIncludes: '/api/v2/limits/status', json: DEMO_LIMITS },
];
